import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ArrowLeft, Award, CheckCircle2, Clock, RefreshCw, XCircle } from 'lucide-react';
import { quizService } from '../../shared/services';
import type { IniciarTentativa, ResultadoTentativa } from '../../shared/types';

function mensagemDeErro(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

function formatarData(valor?: string | null) {
  if (!valor) return '—';
  return new Date(valor).toLocaleString('pt-BR');
}

function calcularTempoRestante(expiraEm: string, agora: number) {
  const restante = new Date(expiraEm).getTime() - agora;
  if (restante <= 0) return 'Tempo encerrado';
  const minutos = Math.floor(restante / 60000);
  const segundos = Math.floor((restante % 60000) / 1000);
  return `${minutos}:${segundos.toString().padStart(2, '0')}`;
}

export function QuizPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [tentativa, setTentativa] = useState<IniciarTentativa | null>(null);
  const [respostas, setRespostas] = useState<Record<string, string[]>>({});
  const [resultado, setResultado] = useState<ResultadoTentativa | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [agora, setAgora] = useState(0);

  useEffect(() => {
    if (!tentativa?.expiraEm) return;
    const timer = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [tentativa?.expiraEm]);

  const { data: quiz, isLoading, isError } = useQuery({
    queryKey: ['quiz-curso', id],
    queryFn: () => quizService.getDoCurso(id!),
    enabled: !!id,
    retry: false,
  });

  const { data: tentativas = [] } = useQuery({
    queryKey: ['tentativas-quiz', id],
    queryFn: () => quizService.minhasTentativas(id!),
    enabled: !!id,
  });

  const iniciar = useMutation({
    mutationFn: () => quizService.iniciar(id!),
    onSuccess: (dados) => {
      setTentativa(dados);
      setRespostas({});
      setResultado(null);
      setErro(null);
      setAgora(Date.now());
    },    onError: (error) => setErro(mensagemDeErro(error, 'Não foi possível iniciar a avaliação.')),
  });

  const submeter = useMutation({
    mutationFn: () => {
      if (!tentativa) throw new Error('Tentativa inválida');
      return quizService.submeter(
        tentativa.tentativaId,
        tentativa.quiz.questoes.map((questao) => ({
          questaoId: questao.id!,
          opcoesIds: respostas[questao.id!] ?? [],
        })),
      );
    },
    onSuccess: (dados) => {
      setResultado(dados);
      setTentativa(null);
      setErro(null);
      queryClient.invalidateQueries({ queryKey: ['tentativas-quiz', id] });
      queryClient.invalidateQueries({ queryKey: ['progresso-resumo'] });
      queryClient.invalidateQueries({ queryKey: ['certificados'] });
      queryClient.invalidateQueries({ queryKey: ['curso-completo'] });
      queryClient.invalidateQueries({ queryKey: ['minhas-trilhas'] });
    },
    onError: (error) => setErro(mensagemDeErro(error, 'Não foi possível enviar a avaliação.')),
  });

  const tempoRestante = tentativa?.expiraEm
    ? calcularTempoRestante(tentativa.expiraEm, agora)
    : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isError || !quiz) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Link to={id ? `/cursos/${id}` : '/catalogo'} className="inline-flex items-center gap-2 text-primary-600 mb-6">
          <ArrowLeft size={16} /> Voltar ao curso
        </Link>
        <div className="card text-center py-12">
          <Award className="mx-auto text-gray-400 mb-4" size={56} />
          <h1 className="text-xl font-semibold text-gray-900">Este curso não possui avaliação</h1>
          <p className="text-gray-600 mt-2">A avaliação final será publicada pelo instrutor.</p>
        </div>
      </div>
    );
  }

  const alternarOpcao = (questaoId: string, opcaoId: string) => {
    setRespostas((atual) => {
      const selecionadas = new Set(atual[questaoId] ?? []);
      if (selecionadas.has(opcaoId)) selecionadas.delete(opcaoId);
      else selecionadas.add(opcaoId);
      return { ...atual, [questaoId]: Array.from(selecionadas) };
    });
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <Link to={`/cursos/${id}`} className="inline-flex items-center gap-2 text-primary-600 mb-6">
          <ArrowLeft size={16} /> Voltar ao curso
        </Link>

        <div className="card">
          <h1 className="text-2xl font-bold text-gray-900">{quiz.titulo}</h1>
          {quiz.descricao && <p className="text-gray-600 mt-2">{quiz.descricao}</p>}

          <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-600">
            <span>Nota mínima: <strong>{quiz.notaMinima}%</strong></span>
            <span>
              Tentativas: <strong>{quiz.tentativasMaximas ?? 'sem limite'}</strong>
            </span>
            {quiz.tempoLimiteMinutos && <span>Tempo: <strong>{quiz.tempoLimiteMinutos} min</strong></span>}
            <span>Questões: <strong>{quiz.questoes.length}</strong></span>
          </div>

          {erro && <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>}

          {!tentativa && !resultado && (
            <div className="mt-6">
              <button
                type="button"
                onClick={() => iniciar.mutate()}
                disabled={iniciar.isPending}
                className="btn-primary disabled:opacity-60"
              >
                {iniciar.isPending ? 'Preparando...' : 'Iniciar avaliação'}
              </button>

              {tentativas.length > 0 && (
                <div className="mt-6">
                  <h2 className="text-sm font-semibold text-gray-700 mb-2">Suas tentativas</h2>
                  <ul className="divide-y divide-gray-100 text-sm">
                    {tentativas.map((t) => (
                      <li key={t.id} className="py-2 flex items-center justify-between gap-3">
                        <span className="text-gray-600">{formatarData(t.iniciadaEm)}</span>
                        <span className={t.aprovado ? 'text-green-700' : 'text-gray-600'}>
                          {t.percentual != null ? `${t.percentual}%` : '—'} · {t.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {tentativa && (
            <form
              className="mt-6"
              onSubmit={(event) => {
                event.preventDefault();
                submeter.mutate();
              }}
            >
              <div className="flex items-center justify-between mb-4 text-sm">
                <span className="text-gray-600">
                  Tentativa {tentativa.tentativaNumero}
                  {tentativa.tentativasRestantes != null && ` · ${tentativa.tentativasRestantes} restante(s)`}
                </span>
                {tempoRestante && (
                  <span className={`inline-flex items-center gap-1 font-mono ${tempoRestante === 'Tempo encerrado' ? 'text-red-600' : 'text-gray-700'}`}>
                    <Clock size={14} /> {tempoRestante}
                  </span>
                )}
              </div>

              <div className="space-y-6">
                {tentativa.quiz.questoes.map((questao, indice) => (
                  <fieldset key={questao.id} className="border border-gray-200 rounded-lg p-4">
                    <legend className="px-1 text-sm font-semibold text-gray-900">
                      {indice + 1}. {questao.enunciado} <span className="text-gray-400">({questao.pontos} pt)</span>
                    </legend>
                    <div className="mt-3 space-y-2">
                      {questao.opcoes.map((opcao) => (
                        <label key={opcao.id} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(respostas[questao.id!] ?? []).includes(opcao.id!)}
                            onChange={() => alternarOpcao(questao.id!, opcao.id!)}
                            className="mt-0.5"
                          />
                          <span>{opcao.texto}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>

              <div className="mt-6 flex gap-3">
                <button type="submit" disabled={submeter.isPending} className="btn-primary disabled:opacity-60">
                  {submeter.isPending ? 'Corrigindo...' : 'Enviar respostas'}
                </button>
                <button
                  type="button"
                  onClick={() => setTentativa(null)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {resultado && (
            <div className="mt-6">
              <div className={`rounded-lg p-5 ${resultado.aprovado ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-center gap-3">
                  {resultado.aprovado ? (
                    <CheckCircle2 className="text-green-600" size={32} />
                  ) : (
                    <XCircle className="text-red-600" size={32} />
                  )}
                  <div>
                    <p className={`text-xl font-bold ${resultado.aprovado ? 'text-green-800' : 'text-red-800'}`}>
                      {resultado.aprovado ? 'Aprovado' : 'Ainda não aprovado'}
                    </p>
                    <p className="text-sm text-gray-700">
                      {resultado.percentual}% · {resultado.pontosObtidos} de {resultado.pontosMaximos} pontos
                      {resultado.tentativasRestantes != null && ` · ${resultado.tentativasRestantes} tentativa(s) restante(s)`}
                    </p>
                  </div>
                </div>
                {resultado.cursoConcluido && (
                  <p className="mt-3 text-sm text-green-800">
                    Curso concluído. Seu certificado está disponível em <Link to="/certificados" className="underline">Meus Certificados</Link>.
                  </p>
                )}
              </div>

              <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Correção</h2>
              <div className="space-y-3">
                {quiz.questoes.map((questao) => {
                  const correcao = resultado.questoes.find((r) => r.questaoId === questao.id);
                  return (
                    <div key={questao.id} className={`border rounded-lg p-4 ${correcao?.correta ? 'border-green-200' : 'border-red-200'}`}>
                      <p className="text-sm font-medium text-gray-900">{questao.enunciado}</p>
                      <p className={`text-sm mt-1 ${correcao?.correta ? 'text-green-700' : 'text-red-700'}`}>
                        {correcao?.correta ? 'Resposta correta' : 'Resposta incorreta'}
                      </p>
                      {!correcao?.correta && (
                        <p className="text-xs text-gray-600 mt-1">
                          Corretas: {questao.opcoes.filter((o) => correcao?.opcoesCorretas.includes(o.id!)).map((o) => o.texto).join('; ')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {!resultado.aprovado && (resultado.tentativasRestantes == null || resultado.tentativasRestantes > 0) && (
                <button
                  type="button"
                  onClick={() => iniciar.mutate()}
                  className="btn-secondary mt-6 inline-flex items-center gap-2"
                >
                  <RefreshCw size={16} /> Tentar novamente
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
